import https from 'https'
import qs from 'qs'
import { coreUtils, axiosUtils, webUtils, logger } from '@ustra/core'
import { isMatchPatterns } from '@ustra/core/src/utils/string-utils'
import { HttpMethod } from '@ustra/core/src/server/http/const'
import CookieStorage from '@ustra/core/src/utils/storage/cookie-storage'
import * as sseUtils from '@ustra/core/src/utils/sse-utils'
import {
  ExtendsAxiosResponse,
  AxiosInstance,
  DebugInterceptorMode,
  AxiosRequestInterceptorArgs,
  AxiosResponseInterceptorArgs,
  InterceptorScope,
} from '@ustra/core/src/utils/axios-utils'
import { globalStorage } from '@ustra/core/src/utils/storage/global-storage'
import { LogLevel } from '@ustra/core/src/config/config-properties'
import { HttpNetworkError } from '@ustra/core/src/error/'
import { base64Converter } from '@ustra/core/src/utils/converter/base64-converter'
import { UrlBuilder } from '@ustra/core/src/utils/url-builder'
import progressEventBus from '../../event-bus/global/progress-event-bus'
import { NuxtProperties } from '../../../config/nuxt-app-properties'
import { UstraPlugin } from '../ustra-plugin'

const STORAGE_KEY = '__AXIOS_INSTANCE__'

const cookieStorage = new CookieStorage({
  samesite: 'Lax',
  secure: false,
  path: '/',
  httponly: false,
  valueConverter: null,
  keyConverter: null,
  pure: true,
  maxAge: -1,
})

/**
 * 전역 설정 옵션
 */
export interface ApiConfigurableOption {
  /**
   * base url
   */
  baseUrl?: string

  /**
   * timeout
   * @default 20000
   */
  timeout?: number

  /**
   * header 객체
   */
  headers?: any

  /**
   * 로딩 바 display 여부
   */
  showLoading?: boolean
}

/**
 * API 호출 옵션
 */
export interface ApiCallOption extends ApiConfigurableOption {
  /**
   * API 호출 URL
   */
  url: string

  /**
   * http Method
   * @default {HttpMethod.GET}
   */
  method?: HttpMethod

  /**
   * end data
   */
  data?: any

  /**
   * 거래아이디(undefined일 경우 신규 생성)
   */
  tranId?: string

  /**
   * 응답유형
   */
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream'

  /**
   * JSONP 호출 여부
   */
  jsonp?: boolean

  /**
   * 호출 파라메터
   */
  params?: any

  /**
   * API 응답 코드 오류 시 PASS 여부
   */
  passOnResponseError?: boolean

  /**
   * 특정 응답 코드일 경우, 오류 처리하지 않음.
   */
  passOnResponseCode?: string[]

  /**
   * 인증 검증 제외 여부
   */
  excludeAuthValidation?: boolean
}

/**
 *
 * @export
 * @class ApiPlugin
 */
export class ApiPlugin {
  private $ustra: UstraPlugin = null
  private createdInstance: boolean = false

  constructor($ustra: UstraPlugin = null) {
    this.$ustra = $ustra
  }

  /**
   * 인터셉터 추가
   *  auth 모듈 등록 후에 추가 필요함.
   */
  addInterceptor() {
    this.applyResCodeCheckingInterceptor(this.$ustra.env.appProp.nuxt)
    this.applyGlobalRequestInterceptor()
  }

  private getApiRequestOption(): ApiConfigurableOption {
    try {
      if (!this.$ustra?.env?.appProp) {
        return null
      }

      const appProp: NuxtProperties = this.$ustra.env.appProp.nuxt

      if (!appProp) {
        return null
      }

      if (appProp.api) {
        return appProp.api
      }

      return null
    } catch (e) {
      return null
    }
  }

  private normalizeCallOption(
    option: ApiCallOption,
  ): axiosUtils.ExtendAxiosRequestConfig & { showLoading: boolean; passOnResponseError: boolean; passOnResponseCode: string[]; excludeAuthValidation: boolean; } {
    const mergedOption = coreUtils.deepMerge<ApiCallOption>(
      {
        data: null,
        url: null,
        method: HttpMethod.GET,
        tranId: coreUtils.uuid(),
        timeout: 20000,
        headers: {},
        showLoading: webUtils.isBrowser(),
        passOnResponseError: false,
        passOnResponseCode: [],
        jsonp: false,
        excludeAuthValidation: false,
      },
      // @ts-ignore
      this.getApiRequestOption() || { url: undefined },
      option,
    )

    return {
      axiosInstance: this.getAxios(),
      url: mergedOption.url,
      data: mergedOption.data,
      method: mergedOption.method,
      timeout: mergedOption.timeout,
      baseURL: mergedOption.baseUrl,
      headers: mergedOption.headers,
      responseType: mergedOption.responseType,
      showLoading: mergedOption.showLoading,
      passOnResponseError: mergedOption.passOnResponseError,
      passOnResponseCode: mergedOption.passOnResponseCode,
      jsonp: mergedOption.jsonp,
      params: mergedOption.params,
      excludeAuthValidation: mergedOption.excludeAuthValidation,
    }
  }

  private getAxios() {
    let axiosInstance = globalStorage.getItem(STORAGE_KEY) as AxiosInstance

    if (!axiosInstance) {
      axiosInstance = this.createAxiosInstance()
      this.createdInstance = true
      globalStorage.setItem(STORAGE_KEY, axiosInstance)
      logger().debug('created axios instance')
    }

    return axiosInstance
  }

  private createAxiosInstance() {
    // const debugInterceptorMode = env.utils.isProduction() ? DebugInterceptorMode.ONLY_ERROR : webUtils.isBrowser() ? DebugInterceptorMode.ONLY_ERROR : DebugInterceptorMode.ALL
    const debugInterceptorMode = DebugInterceptorMode.ONLY_ERROR

    return axiosUtils.createInstance({
      // TODO: 옵션화 처리
      debugInterceptorMode,
      httpsAgent: new https.Agent({
        rejectUnauthorized: false,
      }),
    })
  }

  /**
   * add request interceptor
   * @param interceptor
   */
  addRequestInterceptor = (interceptor: AxiosRequestInterceptorArgs) => {
    return axiosUtils.addRequestInterceptor(interceptor, this.createdInstance ? this.getAxios() : null)
  }

  /**
   * * add response interceptor
   * @param interceptor
   */
  addResponseInterceptor = (interceptor: AxiosResponseInterceptorArgs) => {
    return axiosUtils.addResponseInterceptor(interceptor, this.createdInstance ? this.getAxios() : null)
  }

  /**
   * API Call
   * @param option {ApiCallOption}
   */
  async call<T = any>(option: ApiCallOption | string): Promise<ExtendsAxiosResponse<T>> {
    const axiosOption = this.normalizeCallOption(typeof option === 'string' ? { url: option } : option)

    try {
      if (axiosOption.showLoading) {
        progressEventBus.emit({ show: true })
      }

      const result = await axiosUtils.call<T>(axiosOption)
      return result
    } finally {
      if (axiosOption.showLoading) {
        progressEventBus.emit({ show: false })
      }
    }
  }

  /**
   * URL encoded parameter를 생성한다.
   * @param params 파라메터
   * @param encodeBase64 base64 인코딩 여부
   */
  createUrlEncodedParameter(params: object, encodeBase64: boolean = false) {
    for (const key in params) {
      if (typeof params[key] === 'object') {
        params[key] = JSON.stringify(params[key])
      }

      if (encodeBase64) {
        params[key] = base64Converter.convert(params[key])
      }
    }

    return qs.stringify(params)
  }

  /**
   * UrlBuilder를 생성한다.
   * @param baseUrl base URL
   * @returns UrlBuilder
   */
  urlBuilder(baseUrl: string) {
    return new UrlBuilder(baseUrl)
  }

  /**
   * ssemitter 연결을 수행한다.
   * @param option {SseEmitterOption} 연결 옵션
   */
  sse(option: sseUtils.SseEmitterOption) {
    return sseUtils.connect(option)
  }

  private completedCheckTimer: any = null
  /**
   * 서버 요청 응답 시 처리 call back 전달
   * @param callback
   */
  addRequestCompletedCallback(callback: Function) {
    if (this.completedCheckTimer) {
      try {
        clearInterval(this.completedCheckTimer)
      } catch (e) { }
    }

    cookieStorage.setItem('RES_ATTATCHED', '0')

    this.completedCheckTimer = setInterval(() => {
      const resAttatched = cookieStorage.getItem('RES_ATTATCHED')

      if (resAttatched === '1' || resAttatched === 1) {
        clearInterval(this.completedCheckTimer)
        callback()
      }
    }, 500)
  }

  /**
   * API 오류 처리 interceptor를 등록한다.
   * @param appProp
   */
  private applyResCodeCheckingInterceptor(appProp: NuxtProperties) {
    // 응답 코드 검증 처리 하지 않을 경우
    if (!appProp.api?.occurErrorwhenReceivedApiErrCode) {
      return
    }

    this.addResponseInterceptor({
      key: 'api-res-error-process-interceptor',
      scope: InterceptorScope.BOTH,
      processAllError: true,
      onFulfilled: res => {
        const addDetailErrorLog = this.$ustra.env.appProp.logger.level >= LogLevel.Debug

        // 검증을 수행하지 않을 URL 처리
        if (appProp.api?.ignoreErrorUrlPatterns) {
          if (isMatchPatterns(res.config.url, ...appProp.api.ignoreErrorUrlPatterns)) {
            return res
          }
        }

        // @ts-ignore
        if (res && res.data && res.data.resultCode && !res.config.passOnResponseError) {
          // @ts-ignore
          if (res.data.resultCode !== '0000' && !res.config.passOnResponseCode.some(responseCode => responseCode === res.data.resultCode)) {
            let message = res.data.resultMessage

            if (addDetailErrorLog && res.data.errors) {
              for (const error of res.data.errors) {
                message += '/' + error
              }
            }
            const error = new HttpNetworkError(new Error(message), res.data.resultCode, res.config)
            console.log({ error })
            throw error
          }
        }
        return res
      },
      onRejected: error => {
        error.message = axiosUtils.getAxiosErrorMessage(error)

        console.log(error?.config, error)
        // TODO  body 추출할 것

        throw new HttpNetworkError(error, null)
      },
    })
  }

  /**
   * global api request interceptor를 등록한다.
   */
  private applyGlobalRequestInterceptor() {
    this.addRequestInterceptor({
      key: 'api-global-request-interceptor',
      scope: InterceptorScope.BOTH,
      onFulfilled: config => {
        const appProp = this.$ustra.env?.appProp

        if (appProp) {
          if (process.server) {
            config.baseURL = config.baseURL || appProp.server.apiServerBaseUrl
          }

          // profile header 추가
          config.headers.c = this.$ustra.env.encryptedProfile

          // rest header 값을 Http Header에 추가
          if (config.data && config.data.header) {
            try {
              config.headers['rest-api-header'] = JSON.stringify(config.data.header)
            } catch (e) { }
          }
        }
      },
    })
  }
}
